import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import athleteApi from '@/lib/api'
import { INPUT_CLASSES } from '@/lib/theme'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'

interface BrandsSettingsProps {
  athlete: any
}

interface Manager {
  id: number // connection id
  manager: {
    id: string
    name: string
    email: string
    avatar: string | null
  }
  status: 'pending' | 'connected' | 'blocked'
  isInitiator: boolean
  createdAt: string
}

interface PendingInvite {
  id: number
  email: string
  status: string
  created_at: string
}

interface TeamMembership {
  teamId: string
  name: string | null
  logo: string | null
  isPrimary: boolean
  permissions: Record<string, boolean>
}

const BrandsSettings = ({ athlete }: BrandsSettingsProps) => {
  const [managers, setManagers] = useState<Manager[]>([])
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(false)
  const [teams, setTeams] = useState<TeamMembership[]>([])
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [updatingPermission, setUpdatingPermission] = useState<string | null>(null)

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isInviting, setIsInviting] = useState(false)

  const fetchManagers = async () => {
    setLoading(true)
    try {
      const [connectionsRes, invitesRes] = await Promise.all([
        athleteApi.get('/athlete/managers'),
        athleteApi.get('/athlete/managers/invitations'),
      ])
      setManagers(connectionsRes.data.managers)
      // Only show invitations that are still pending (not yet accepted)
      setPendingInvites(
        (invitesRes.data.invitations || []).filter((i: PendingInvite) => i.status === 'pending'),
      )
    } catch (error) {
      console.error('Failed to fetch managers', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTeams = async () => {
    setTeamsLoading(true)
    try {
      const res = await athleteApi.get('/athlete/managers/teams')
      setTeams(res.data.teams ?? [])
    } catch {
      console.error('Failed to fetch teams')
    } finally {
      setTeamsLoading(false)
    }
  }

  const handleTogglePermission = async (
    teamId: string,
    permission: string,
    currentValue: boolean,
  ) => {
    setUpdatingPermission(`${teamId}:${permission}`)
    try {
      const res = await athleteApi.patch(`/athlete/managers/teams/${teamId}/permissions`, {
        [permission]: !currentValue,
      })
      setTeams((prev) =>
        prev.map((t) => (t.teamId === teamId ? { ...t, permissions: res.data.permissions } : t)),
      )
      toast(!currentValue ? 'Permission granted' : 'Permission revoked')
    } catch {
      toast.error('Error', { description: 'Failed to update permission' })
    } finally {
      setUpdatingPermission(null)
    }
  }

  useEffect(() => {
    fetchManagers()
    fetchTeams()
  }, [])

  const handleInviteManager = async () => {
    if (!inviteEmail) return
    setIsInviting(true)
    try {
      const response = await athleteApi.post('/athlete/managers/connect', { email: inviteEmail })

      if (response.status === 202) {
        // Manager doesn't have an account yet — invitation email sent
        toast('Invitation sent', {
          description: `They'll get an email to join Athios and will be connected to you automatically when they sign up.`,
        })
      } else {
        // 201 — existing manager account, connection request created
        toast('Connection request sent', { description: `Request sent to ${inviteEmail}` })
      }

      setInviteEmail('')
      setIsDialogOpen(false)
      fetchManagers()
    } catch (error: any) {
      const msg: string = error.response?.data?.error || 'Failed to send invitation'

      if (msg.toLowerCase().includes('athlete account')) {
        toast.error('Wrong account type', {
          description: 'That email belongs to an athlete account, not a manager account.',
        })
      } else {
        toast.error('Error', { description: msg })
      }
    } finally {
      setIsInviting(false)
    }
  }

  const handleRemoveManager = async (connectionId: number) => {
    try {
      await athleteApi.delete(`/athlete/managers/${connectionId}`)
      toast('Manager removed', { description: 'Connection has been removed' })
      fetchManagers()
    } catch (error) {
      toast.error('Error', { description: 'Failed to remove manager' })
    }
  }

  const handleAcceptRequest = async (connectionId: number) => {
    try {
      await athleteApi.patch(`/athlete/managers/${connectionId}`, { status: 'connected' })
      toast('Connected', { description: 'You are now connected to this manager' })
      fetchManagers()
    } catch (error) {
      toast.error('Error', { description: 'Failed to accept request' })
    }
  }

  const hasAny = managers.length > 0 || pendingInvites.length > 0

  return (
    <div className="space-y-6">
      {/* Brand Connections Card (kept as placeholder/is for now) */}

      {/* Manager Connections Card */}
      <Card variant="dark" className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white">Manager Connections</CardTitle>
            <CardDescription className="text-gray-400">
              Manage your connected talent managers
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="lime">
                <Plus className="h-4 w-4 mr-2" />
                Add Manager
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a2b3c] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>Connect with a Manager</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Enter the email address of your talent manager. If they don't have an account
                  yet, we'll send them an invitation to join Athios.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="email" className="text-white">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="manager@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className={INPUT_CLASSES.dark}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setIsDialogOpen(false)}
                  className="text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteManager}
                  disabled={isInviting || !inviteEmail}
                  variant="lime"
                >
                  {isInviting ? 'Sending...' : 'Send Request'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading managers...</div>
          ) : !hasAny ? (
            <div className="text-center py-8 text-gray-400">
              <p className="mb-4">No manager connections yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active / pending connections (manager has an account) */}
              {managers.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-10 w-10 border border-white/10">
                      <AvatarImage src={item.manager.avatar || undefined} />
                      <AvatarFallback className="bg-[#2A3C4D] text-white">
                        {item.manager.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium text-white flex items-center gap-2">
                        {item.manager.name}
                        {item.status === 'connected' && (
                          <Badge
                            variant="outline"
                            className="border-green-500/50 text-green-400 bg-green-500/10 text-xs px-2 py-0"
                          >
                            Connected
                          </Badge>
                        )}
                        {item.status === 'pending' && (
                          <Badge
                            variant="outline"
                            className="border-yellow-500/50 text-yellow-400 bg-yellow-500/10 text-xs px-2 py-0"
                          >
                            Pending {item.isInitiator ? '(Sent)' : '(Received)'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{item.manager.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {item.status === 'pending' && !item.isInitiator && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-8"
                        onClick={() => handleAcceptRequest(item.id)}
                      >
                        Accept
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-gray-400 hover:text-red-400 hover:bg-red-900/20 h-8 w-8"
                      onClick={() => handleRemoveManager(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Pending invitations — manager hasn't registered yet */}
              {pendingInvites.map((invite) => (
                <div
                  key={`invite-${invite.id}`}
                  className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-10 w-10 border border-white/10">
                      <AvatarFallback className="bg-[#2A3C4D] text-white">
                        {invite.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium text-white flex items-center gap-2">
                        {invite.email}
                        <Badge
                          variant="outline"
                          className="border-yellow-500/50 text-yellow-400 bg-yellow-500/10 text-xs px-2 py-0"
                        >
                          Invite sent — awaiting registration
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-400">Invitation email sent</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Management Team Permissions */}
      {teams.length > 0 && (
        <Card variant="dark" className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-white">Management Team Permissions</CardTitle>
            <CardDescription className="text-gray-400">
              Control what your management teams can do on your behalf
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {teamsLoading ? (
              <div className="text-center py-4 text-gray-400">Loading teams...</div>
            ) : (
              teams.map((team) => (
                <div
                  key={team.teamId}
                  className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 border border-white/10">
                      <AvatarImage src={team.logo || undefined} />
                      <AvatarFallback className="bg-[#2A3C4D] text-white text-xs">
                        {(team.name ?? '?').charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="font-medium text-white flex items-center gap-2">
                      {team.name ?? 'Unnamed team'}
                      {team.isPrimary && (
                        <Badge
                          variant="outline"
                          className="border-[#C0FD72]/50 text-[#C0FD72] bg-[#C0FD72]/10 text-xs px-2 py-0"
                        >
                          Primary
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Accept &amp; decline offers</p>
                      <p className="text-xs text-gray-400">
                        Allow this team to accept or decline collaboration offers on your behalf
                      </p>
                    </div>
                    <Switch
                      checked={!!team.permissions.accept_collaborations}
                      disabled={updatingPermission === `${team.teamId}:accept_collaborations`}
                      className="data-[state=checked]:bg-[#C0FD72]"
                      onCheckedChange={() =>
                        handleTogglePermission(
                          team.teamId,
                          'accept_collaborations',
                          !!team.permissions.accept_collaborations,
                        )
                      }
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default BrandsSettings
